import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { VideoThumbnailService } from '../components/services/videoThumbnailService';
import { RefreshCw, CheckCircle, XCircle, Loader } from 'lucide-react';

interface MigrationResult {
  videoId: string;
  title: string;
  status: 'pending' | 'processing' | 'success' | 'error' | 'skipped';
  message?: string;
  thumbnailPath?: string;
}

interface VideoData {
  id: string;
  title: string;
  storagePath: string;
  thumbnailPath?: string;
  [key: string]: any;
}

const ThumbnailMigrationUtility: React.FC = () => {
  const [results, setResults] = useState<MigrationResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [forceRegenerate, setForceRegenerate] = useState(true); // Default ON so existing thumbnails are refreshed

  const updateResult = (videoId: string, updates: Partial<MigrationResult>) => {
    setResults(prev => prev.map(r => 
      r.videoId === videoId ? { ...r, ...updates } : r
    ));
  };

  const generateThumbnailsForAllVideos = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      alert('Please sign in first!');
      return;
    }

    setIsRunning(true);
    setResults([]);
    setProgress({ current: 0, total: 0 });

    try {
      console.log('🟡 Starting thumbnail migration...');
      
      // Get all videos for the current user
      const videosRef = collection(db, `users/${user.uid}/videos`);
      const videosSnapshot = await getDocs(videosRef);
      
      const videos: VideoData[] = videosSnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title || 'Untitled',
        storagePath: doc.data().storagePath || '',
        thumbnailPath: doc.data().thumbnailPath,
        ...doc.data()
      }));

      console.log(`🟢 Found ${videos.length} videos`);
      setProgress({ current: 0, total: videos.length });

      // Initialize results
      const initialResults: MigrationResult[] = videos.map(video => ({
        videoId: video.id,
        title: video.title,
        status: 'pending'
      }));
      setResults(initialResults);

      // Process each video
      for (let i = 0; i < videos.length; i++) {
        const video = videos[i];
        
        try {
          console.log(`\n📹 Processing video ${i + 1}/${videos.length}: ${video.title}`);
          
          updateResult(video.id, { status: 'processing' });

          // Check if thumbnail already exists (unless force regenerate is enabled)
          if (video.thumbnailPath && !forceRegenerate) {
            console.log('⏭️  Video already has thumbnail, skipping...');
            updateResult(video.id, {
              status: 'skipped',
              message: 'Already has thumbnail',
              thumbnailPath: video.thumbnailPath
            });
            setProgress({ current: i + 1, total: videos.length });
            continue;
          }

          // If force regenerate and has existing thumbnail, note it
          if (video.thumbnailPath && forceRegenerate) {
            console.log('🔄 Regenerating existing thumbnail...');
          }

          // Check if storagePath exists
          if (!video.storagePath) {
            console.error('❌ Video has no storagePath');
            updateResult(video.id, {
              status: 'error',
              message: 'No storage path found'
            });
            setProgress({ current: i + 1, total: videos.length });
            continue;
          }

          // Get video download URL
          console.log('🟡 Getting video download URL...');
          const videoRef = ref(storage, video.storagePath);
          const videoUrl = await getDownloadURL(videoRef);
          console.log('🟢 Video URL obtained');

          // Generate thumbnail from video URL
          console.log('🟡 Generating thumbnail...');
          const thumbnailPath = await VideoThumbnailService.generateThumbnailFromUrl(
            videoUrl,
            user.uid,
            video.id
          );
          console.log('🟢 Thumbnail generated:', thumbnailPath);

          // Update Firestore document
          console.log('🟡 Updating Firestore...');
          const videoDocRef = doc(db, `users/${user.uid}/videos/${video.id}`);
          await updateDoc(videoDocRef, {
            thumbnailPath: thumbnailPath,
            updatedAt: serverTimestamp()
          });
          console.log('🟢 Firestore updated');

          updateResult(video.id, {
            status: 'success',
            message: forceRegenerate && video.thumbnailPath 
              ? 'Thumbnail regenerated successfully' 
              : 'Thumbnail created successfully',
            thumbnailPath: thumbnailPath
          });

        } catch (error) {
          console.error(`❌ Failed to process video ${video.title}:`, error);
          updateResult(video.id, {
            status: 'error',
            message: error instanceof Error ? error.message : 'Unknown error'
          });
        }

        setProgress({ current: i + 1, total: videos.length });
        
        // Small delay to avoid overwhelming Firebase
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      console.log('\n🎉 Migration complete!');
      alert('Thumbnail migration complete! Check the results below.');

    } catch (error) {
      console.error('❌ Migration failed:', error);
      alert('Migration failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsRunning(false);
    }
  };

  const getStatusIcon = (status: MigrationResult['status']) => {
    switch (status) {
      case 'pending':
        return <div style={{ width: '20px', height: '20px', border: '2px solid #d1d5db', borderRadius: '50%' }} />;
      case 'processing':
        return <Loader size={20} color="#2563eb" className="spin" />;
      case 'success':
        return <CheckCircle size={20} color="#10b981" />;
      case 'error':
        return <XCircle size={20} color="#ef4444" />;
      case 'skipped':
        return <CheckCircle size={20} color="#f59e0b" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: MigrationResult['status']) => {
    switch (status) {
      case 'processing':
        return '#dbeafe';
      case 'success':
        return '#d1fae5';
      case 'error':
        return '#fee2e2';
      case 'skipped':
        return '#fef3c7';
      default:
        return '#f9fafb';
    }
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const skippedCount = results.filter(r => r.status === 'skipped').length;

  return (
    <div style={{
      maxWidth: '800px',
      margin: '2rem auto',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '1rem',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', fontWeight: '700' }}>
          🖼️ Thumbnail Migration Utility
        </h1>
        <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '0.875rem' }}>
          Generate thumbnails for your videos, including regenerating existing ones with improved quality.
        </p>

        {/* Info Box */}
        <div style={{
          padding: '1rem',
          backgroundColor: '#dbeafe',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
          fontSize: '0.875rem',
          color: '#1e40af'
        }}>
          <strong>ℹ️ What this does:</strong>
          <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0 }}>
            <li>Scans all videos in your library</li>
            <li>{forceRegenerate ? 'Regenerates ALL thumbnails (even existing ones)' : 'Skips videos that already have thumbnails'}</li>
            <li>Generates thumbnails at 25% into video (smart capture)</li>
            <li>Uploads thumbnails to Firebase Storage</li>
            <li>Updates Firestore with thumbnail paths</li>
          </ul>
        </div>

        {/* Force Regenerate Toggle */}
        <div style={{
          padding: '1rem',
          backgroundColor: forceRegenerate ? '#fef3c7' : '#f9fafb',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          cursor: 'pointer',
          border: `2px solid ${forceRegenerate ? '#f59e0b' : '#e5e7eb'}`,
          transition: 'all 0.2s'
        }}
        onClick={() => !isRunning && setForceRegenerate(!forceRegenerate)}
        >
          <input
            type="checkbox"
            checked={forceRegenerate}
            onChange={(e) => setForceRegenerate(e.target.checked)}
            disabled={isRunning}
            style={{
              width: '20px',
              height: '20px',
              cursor: isRunning ? 'not-allowed' : 'pointer'
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
              🔄 Force Regenerate All Thumbnails
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Enable this to regenerate thumbnails for ALL videos, even those that already have them. 
              Useful for updating to the new 25% capture position.
            </div>
          </div>
        </div>

        {/* Start Button */}
        <button
          onClick={generateThumbnailsForAllVideos}
          disabled={isRunning}
          style={{
            width: '100%',
            padding: '0.875rem 1.5rem',
            backgroundColor: isRunning ? '#9ca3af' : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!isRunning) e.currentTarget.style.backgroundColor = '#1d4ed8';
          }}
          onMouseLeave={(e) => {
            if (!isRunning) e.currentTarget.style.backgroundColor = '#2563eb';
          }}
        >
          <RefreshCw size={20} className={isRunning ? 'spin' : ''} />
          {isRunning ? 'Generating Thumbnails...' : 'Start Thumbnail Generation'}
        </button>

        {/* Progress */}
        {progress.total > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              color: '#6b7280'
            }}>
              <span>Progress: {progress.current} / {progress.total}</span>
              <span>{Math.round((progress.current / progress.total) * 100)}%</span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(progress.current / progress.total) * 100}%`,
                height: '100%',
                backgroundColor: '#2563eb',
                transition: 'width 0.3s ease'
              }} />
            </div>

            {/* Summary */}
            {progress.current === progress.total && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#15803d'
              }}>
                <strong>✅ Complete!</strong> {successCount} successful, {skippedCount} skipped, {errorCount} errors
              </div>
            )}
          </div>
        )}

        {/* Results List */}
        {results.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              Results
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {results.map(result => (
                <div
                  key={result.videoId}
                  style={{
                    padding: '1rem',
                    backgroundColor: getStatusColor(result.status),
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ flexShrink: 0 }}>
                    {getStatusIcon(result.status)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      {result.title}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {result.message || 'Waiting...'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default ThumbnailMigrationUtility;
